import { useCallback, useEffect, useRef, useState } from "react";
import type { GitIdentity, GitInstallPlan, GitReadiness } from "@racler/protocol";
import { Dialog } from "./Dialog.tsx";
import {
  GIT_SETUP_COPY,
  PREPARE_EXPLANATION,
  runInstall,
  runPrepare,
  safeCheck,
  stepFor,
  type GitSetupApi,
  type GitSetupOutcome,
} from "../lib/gitSetup.ts";

/**
 * The ONE compact Git Setup dialog (Autonomous selection, run start, Create/Add Project). It only renders the typed
 * readiness state and calls the shared actions; it never inspects Git itself.
 *
 *  - `onReady`  READY was reached (the caller continues the original action).
 *  - `onClose`  the user cancelled; the caller keeps the previous state.
 */
export function GitSetupDialog({
  api,
  initial,
  onReady,
  onClose,
}: {
  api: GitSetupApi;
  initial: GitReadiness;
  onReady: () => void;
  onClose: () => void;
}) {
  const [readiness, setReadiness] = useState<GitReadiness>(initial);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [plan, setPlan] = useState<GitInstallPlan | null>(null);
  const [installNote, setInstallNote] = useState<string | null>(null);
  const [needsIdentity, setNeedsIdentity] = useState(false);
  const [identity, setIdentity] = useState<GitIdentity>({ name: "", email: "" });
  const [copied, setCopied] = useState(false);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => void (alive.current = false);
  }, []);

  const step = stepFor(readiness);

  const apply = useCallback(
    (o: GitSetupOutcome) => {
      if (!alive.current) return;
      setReadiness(o.readiness);
      setConfirming(false);
      if (o.needsIdentity) setNeedsIdentity(true);
      if (o.install) {
        setPlan(o.install.plan);
        setInstallNote(o.install.message);
      }
      if (o.readiness.state === "READY") onReady();
    },
    [onReady],
  );

  const guarded = async (fn: () => Promise<void>) => {
    setBusy(true);
    try {
      await fn();
    } finally {
      if (alive.current) setBusy(false);
    }
  };

  // Show the exact manual command (never a fake button) as soon as Git is missing.
  useEffect(() => {
    if (step !== "install" || plan) return;
    api.installPlan().then(
      (p) => alive.current && setPlan(p),
      () => undefined,
    );
  }, [api, step, plan]);

  const recheck = () => guarded(async () => apply({ readiness: await safeCheck(api) }));
  const copy = (text: string) => void navigator.clipboard?.writeText(text).then(() => setCopied(true));
  const identityValid = identity.name.trim() !== "" && identity.email.includes("@");

  return (
    <Dialog title="Git setup required" onClose={onClose}>
      <div className="dialog-body" data-testid="git-setup" data-state={readiness.state}>
        <p>{GIT_SETUP_COPY.why}</p>
        {step === "install" && (
          <>
            <p data-testid="git-setup-body">{GIT_SETUP_COPY.install.body}</p>
            {installNote && <p className="muted small">{installNote}</p>}
            {plan && (
              <>
                <p className="muted small">{plan.instructions}</p>
                {plan.command && (
                  <p>
                    <code data-testid="git-install-command">{plan.command}</code>{" "}
                    {!plan.canLaunch && (
                      <button type="button" onClick={() => copy(plan.command!)} data-testid="git-copy-command">
                        {copied ? "Copied" : "Copy"}
                      </button>
                    )}
                  </p>
                )}
              </>
            )}
          </>
        )}
        {(step === "prepare" || step === "baseline") && (
          <>
            <p data-testid="git-setup-body">{GIT_SETUP_COPY[step].body}</p>
            {confirming && (
              <p className="muted small" data-testid="git-confirm-text">
                {PREPARE_EXPLANATION[step]}
              </p>
            )}
            {needsIdentity && (
              <div data-testid="git-identity">
                <p className="muted small">
                  Git has no name/email for commits. They are used for this one commit only and are not saved to
                  any Git settings.
                </p>
                <input
                  placeholder="Name"
                  value={identity.name}
                  onChange={(e) => setIdentity({ ...identity, name: e.target.value })}
                  data-testid="git-identity-name"
                />
                <input
                  placeholder="Email"
                  value={identity.email}
                  onChange={(e) => setIdentity({ ...identity, email: e.target.value })}
                  data-testid="git-identity-email"
                />
              </div>
            )}
          </>
        )}
        {step === "error" && (
          <p data-testid="git-setup-body">
            {GIT_SETUP_COPY.error.body}
            {readiness.message ? ` ${readiness.message}` : ""}
          </p>
        )}
      </div>
      <div className="row end">
        <button type="button" onClick={onClose} data-testid="git-setup-cancel">
          Cancel
        </button>
        {step === "install" && (
          <>
            {plan?.canLaunch && (
              <button
                type="button"
                className="primary"
                disabled={busy}
                data-testid="git-install"
                onClick={() => void guarded(async () => apply(await runInstall(api)))}
              >
                {GIT_SETUP_COPY.install.action}
              </button>
            )}
            <button type="button" disabled={busy} onClick={() => void recheck()} data-testid="git-recheck">
              Check again
            </button>
          </>
        )}
        {(step === "prepare" || step === "baseline") && (
          <button
            type="button"
            className="primary"
            disabled={busy || (needsIdentity && !identityValid)}
            data-testid="git-prepare"
            onClick={() => {
              // An existing folder is never touched without an explicit, informed confirmation.
              if (!confirming && !needsIdentity) return setConfirming(true);
              void guarded(async () => apply(await runPrepare(api, needsIdentity ? identity : undefined)));
            }}
          >
            {confirming || needsIdentity ? "Confirm" : GIT_SETUP_COPY[step].action}
          </button>
        )}
        {step === "error" && (
          <button
            type="button"
            className="primary"
            disabled={busy}
            onClick={() => void recheck()}
            data-testid="git-recheck"
          >
            {GIT_SETUP_COPY.error.action}
          </button>
        )}
      </div>
    </Dialog>
  );
}
